import * as signalR from '@microsoft/signalr';

class NotificationSignalRService {
    constructor() {
        this.connection = null;
        this.connectionPromise = null;
        this.state = 'disconnected';
        this.stateListeners = new Set();
        this.retryTimer = null;
        this.shouldReconnect = false;
        this.accessTokenFactory = null;
    }

    emitState(state, error = null) {
        this.state = state;
        this.stateListeners.forEach((callback) => callback(state, error));
    }

    onStateChange(callback) {
        this.stateListeners.add(callback);
        callback(this.state, null);
        return () => this.stateListeners.delete(callback);
    }

    async start(accessTokenFactory) {
        if (this.connectionPromise) return this.connectionPromise;
        if (!process.env.REACT_APP_API_URL) throw new Error('REACT_APP_API_URL is not configured');

        const tokenFactory = typeof accessTokenFactory === 'function'
            ? accessTokenFactory
            : async () => accessTokenFactory;
        this.accessTokenFactory = tokenFactory;
        this.shouldReconnect = true;
        this.connection = new signalR.HubConnectionBuilder()
            .withUrl(`${process.env.REACT_APP_API_URL}/notificationhub`, {
                accessTokenFactory: tokenFactory,
                withCredentials: false,
            })
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: ({ previousRetryCount }) => (
                    Math.min(30000, 1000 * (2 ** Math.min(previousRetryCount, 5))) + Math.floor(Math.random() * 1000)
                ),
            })
            .configureLogging(
                process.env.NODE_ENV === 'production' ? signalR.LogLevel.Warning : signalR.LogLevel.Information,
            )
            .build();

        this.connection.onreconnecting((error) => this.emitState('reconnecting', error));
        this.connection.onreconnected(() => this.emitState('connected'));
        this.connection.onclose((error) => {
            this.connection = null;
            this.connectionPromise = null;
            this.emitState('disconnected', error);
            this.scheduleInitialRetry();
        });

        this.emitState('connecting');
        this.connectionPromise = this.connection.start()
            .then(() => {
                this.emitState('connected');
                return this.connection;
            })
            .catch((error) => {
                this.connection = null;
                this.connectionPromise = null;
                this.emitState('disconnected', error);
                this.scheduleInitialRetry();
                throw error;
            });
        return this.connectionPromise;
    }

    scheduleInitialRetry() {
        if (!this.shouldReconnect || this.retryTimer || !this.accessTokenFactory) return;
        this.retryTimer = setTimeout(() => {
            this.retryTimer = null;
            this.start(this.accessTokenFactory).catch(() => {});
        }, 5000);
    }

    async stop() {
        this.shouldReconnect = false;
        this.accessTokenFactory = null;
        if (this.retryTimer) clearTimeout(this.retryTimer);
        this.retryTimer = null;
        const connection = this.connection;
        this.connection = null;
        this.connectionPromise = null;
        if (connection) await connection.stop();
        this.emitState('disconnected');
    }
}

const notificationSignalRService = new NotificationSignalRService();
export default notificationSignalRService;
